/**
 * TrainingLogViewer - Training Job Logs Display
 * 
 * Displays structured logs from training job execution.
 * Supports filtering by log level and search functionality.
 * 
 * Following BRAIN-PRE-CODE-CHECKLIST.md:
 * - Clean single imports
 * - Defensive null checks
 * - Proper error handling
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { mlApiClient } from '../../shared/utils';

interface TrainingLog {
  timestamp: string;
  level: string;
  message: string;
}

interface TrainingLogViewerProps {
  runId: string;
  onClose: () => void;
}

export const TrainingLogViewer: React.FC<TrainingLogViewerProps> = ({ runId, onClose }) => {
  const [logs, setLogs] = useState<TrainingLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [levelFilter, setLevelFilter] = useState<string>('');

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await mlApiClient.getTrainingLogs(runId, 1000, levelFilter || undefined);
      const response = data as { logs: TrainingLog[] };
      setLogs(response.logs || []);
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to load training logs');
    } finally {
      setLoading(false);
    }
  }, [runId, levelFilter]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const getLevelColor = (level: string) => {
    switch (level.toUpperCase()) {
      case 'ERROR':
        return '#ef4444';
      case 'WARNING':
        return '#f59e0b';
      case 'INFO':
        return '#3b82f6';
      case 'DEBUG':
        return '#9ca3af';
      default:
        return '#9ca3af';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch {
      return timestamp;
    }
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Training Logs - {runId}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {/* Filter Controls */}
        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Log Level</InputLabel>
            <Select
              value={levelFilter}
              onChange={(e) => setLevelFilter(e.target.value)}
              label="Log Level"
              size="small"
            >
              <MenuItem value="">All Levels</MenuItem>
              <MenuItem value="INFO">INFO</MenuItem>
              <MenuItem value="WARNING">WARNING</MenuItem>
              <MenuItem value="ERROR">ERROR</MenuItem>
              <MenuItem value="DEBUG">DEBUG</MenuItem>
            </Select>
          </FormControl>
          
          <Typography variant="body2" color="text.secondary">
            {logs.length} log entries
          </Typography>
        </Box>

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && !loading && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Empty State */}
        {!loading && !error && logs.length === 0 && (
          <Alert severity="info">
            No logs found for this training run.
          </Alert>
        )}

        {/* Log Display */}
        {!loading && !error && logs.length > 0 && (
          <Box
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              bgcolor: 'grey.900',
              p: 2,
              borderRadius: 1,
              maxHeight: '60vh',
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'grey.800'
            }}
          >
            {logs.map((log, index) => (
              <Box
                key={index}
                sx={{
                  mb: 0.5,
                  color: getLevelColor(log.level),
                  '&:hover': {
                    bgcolor: 'grey.800',
                    borderRadius: 0.5
                  }
                }}
              >
                <span style={{ color: '#6b7280' }}>[{formatTimestamp(log.timestamp)}]</span>
                {' '}
                <span style={{ fontWeight: 600 }}>[{log.level}]</span>
                {' '}
                <span>{log.message}</span>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TrainingLogViewer;
